/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import {
  Box,
  Checkbox,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FormChangeEvent, UseForm } from "../../types/UseForm";
import { formValue } from "../../services/formServiceFunctions";
import { ChangeEventHandler } from "react";
import ValidatorDateField from "../Validator/ValidatorDatePicker";
import { requiredFieldDefault } from "../../services/validationRules";
import { translate } from "../../services/Translations/translatorService";

type OrganizationHintsFormProps = {
  path: string;
  disabled: boolean;
  translationPath: string;
} & Pick<UseForm, "values" | "handleChange" | "registerValidators" | "handleBlur" | "handleDateChange" | "errors">;
export const OrganizationHintsForm = ({
  values,
  path,
  handleChange,
  disabled,
  registerValidators,
  handleBlur,
  handleDateChange,
  errors,
  translationPath,
}: OrganizationHintsFormProps) => {
  const insolvencyStateLabelId = `${path}insolvenyStateLabel`;
  const isInsolvencyOpen = ["open", "rejected"].includes(formValue(values, "insolvencyStatus", ""));

  const insolvencyStatusPath = `${path}insolvencyStatus`;

  const insolvencyStartDatePath = `${path}insolvencyStartDate`;
  const insolvencyStartDateError = errors[`${path}insolvencyStartDate`];

  const informCasesAboutNewHintsPath = `${path}informCasesAboutNewHints`;

  const hasInsolvency = Boolean(formValue(values, "insolvencyStatus") || formValue(values, "insolvencyStartDate"));

  const strategicHintsValue = formValue(values, "strategicHints", "");

  const updateValue = (path: string, value: FormChangeEvent["target"]["value"]) => {
    handleChange({ target: { name: path, value } });
  };

  const handleInsolvencyCheckboxChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (!e.target.checked) {
      updateValue(insolvencyStatusPath, null);
      updateValue(insolvencyStartDatePath, null);
      // If no insolvency or nor hint make sure to disable inform about new hints because they are none
      !strategicHintsValue && updateValue(informCasesAboutNewHintsPath, null);
      return;
    }

    updateValue(insolvencyStatusPath, "temporarily");
  };

  const handleInsolvencyStatusChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    switch (e.target.value) {
      case "open":
        updateValue(informCasesAboutNewHintsPath, true);
        // Needed because in e.g. DatasetEntityPicker value is missing in initial form values, thus this value would not be validated. Push null in
        updateValue(insolvencyStartDatePath, null);
        break;
      default:
        updateValue(insolvencyStartDatePath, null);
        break;
    }

    handleChange(e);
  };

  return (
    <Stack sx={{ rowGap: "0.5rem" }}>
      <Typography variant={"h4"} align={"center"}>
        Wichtige Hinweise zum Unternehmen
      </Typography>
      <Box>
        <TextField
          name={`${path}strategicHints`}
          value={strategicHintsValue}
          placeholder={translate(translationPath + "strategicHints.placeholder", "")}
          onChange={handleChange}
          disabled={disabled}
          multiline
        />
        <Typography sx={{
          fontSize: "0.75rem"
        }}>{translate(translationPath + "strategicHints.hint", "")}</Typography>
      </Box>
      <FormControlLabel
        control={
          <Checkbox checked={Boolean(hasInsolvency)} onChange={handleInsolvencyCheckboxChange} disabled={disabled} />
        }
        label={translate(translationPath + "hasInsolvency.label", "")}
      />
      {hasInsolvency && (
        <Stack direction={"row"} sx={{ flexWrap: "wrap", columnGap: "1rem" }}>
          <Box>
            <FormLabel id={insolvencyStateLabelId}>
              {translate(translationPath + "insolvencyStatus.label", "")}
            </FormLabel>
            <RadioGroup
              aria-labelledby={insolvencyStateLabelId}
              name={insolvencyStatusPath}
              value={formValue(values, "insolvencyStatus", "")}
              onChange={handleInsolvencyStatusChange}
            >
              <Stack direction={"row"}>
                <FormControlLabel
                  control={<Radio value={"temporarily"} disabled={disabled} />}
                  label={translate(translationPath + "insolvencyStatus.temporarily", "")}
                />
                <FormControlLabel
                  control={<Radio value={"open"} disabled={disabled} />}
                  label={translate(translationPath + "insolvencyStatus.open", "")}
                />
                <FormControlLabel
                  control={<Radio value={"rejected"} disabled={disabled} />}
                  label={translate(translationPath + "insolvencyStatus.rejected", "")}
                />
              </Stack>
            </RadioGroup>
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            {
              <ValidatorDateField
                disabled={!isInsolvencyOpen || disabled}
                sx={{ width: "100%" }}
                label={translate(translationPath + "insolvencyStartDate.label", "")}
                name={insolvencyStartDatePath}
                value={formValue(values, "insolvencyStartDate", "")}
                registerValidators={registerValidators}
                handleBlur={handleBlur}
                onChange={(date) => handleDateChange(date, insolvencyStartDatePath)}
                validators={formValue(values, "insolvencyStartDate", "") === "open" ? requiredFieldDefault : []}
                helperText={insolvencyStartDateError}
                error={!!insolvencyStartDateError}
              />
            }
          </Box>
        </Stack>
      )}
      {!disabled && (
        <FormControlLabel
          control={
            <Checkbox
              name={informCasesAboutNewHintsPath}
              checked={Boolean(formValue(values, "informCasesAboutNewHints"))}
              disabled={!hasInsolvency && !strategicHintsValue}
              onChange={handleChange}
            />
          }
          label={translate(translationPath + "informCasesAboutNewHints.label", "")}
        />
      )}
    </Stack>
  );
};
